import React, { useEffect, useState } from "react";
import { Button, Form, Input } from "antd";
import {
  LockOutlined,
  MailOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import apiClient from "../../actions/api";
import openNotification from "../../components/Notifications/openNotification";
import LogoSrc from "../../assets/image/logo.svg";
import VerificationCodeInput from "./VerificationCodeInput";
import "../../style/auth/auth.scss";

const content = {
  email: {
    eyebrow: "Account recovery",
    title: "Forgot your password?",
    body: "Enter the email connected to your account and we'll send you a verification code.",
  },
  verify: {
    eyebrow: "Email verification",
    title: "Enter your 8-digit code",
    body: "We sent a password reset code to",
  },
  reset: {
    eyebrow: "Create new password",
    title: "Set a new password",
    body: "Choose a secure password you haven't used before.",
  },
};

const ForgotPassword = ({ title, step = "email" }) => {
  document.title = title;
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const [loading, setLoading] = useState(false);
  const resetToken = location.state?.resetToken;
  const email = location.state?.email;

  useEffect(() => {
    if (isAuthenticated) navigate("/dashboard");
    else if (step !== "email" && !resetToken)
      navigate("/forgot-password", { replace: true });
  }, [isAuthenticated, navigate, resetToken, step]);

  const showError = (err) => {
    const errors = err?.response?.data?.errors;
    if (errors?.length)
      errors.forEach(({ msg }) =>
        openNotification("error", "Password reset", msg),
      );
    else
      openNotification(
        "error",
        "Password reset",
        "Something went wrong. Please try again.",
      );
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      if (step === "email") {
        const { data } = await apiClient.post("/users/forgot-password", values);
        navigate("/forgot-password/verify", { state: data });
        openNotification(
          "success",
          "Code sent",
          "Check your email for an 8-digit code.",
        );
      } else if (step === "verify") {
        const { data } = await apiClient.post("/users/verify-password-reset", {
          ...values,
          resetToken,
        });
        navigate("/forgot-password/reset", {
          state: { email, resetToken: data.resetToken },
        });
      } else {
        if (values.password !== values.confirmPassword) {
          openNotification(
            "error",
            "Validation Error",
            "The passwords do not match.",
          );
          return;
        }
        await apiClient.post("/users/reset-password", {
          password: values.password,
          resetToken,
        });
        openNotification(
          "success",
          "Password updated",
          "You can now sign in with your new password.",
        );
        navigate("/login", { replace: true });
      }
    } catch (err) {
      showError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="product-auth-page">
      <div className="product-auth-shell">
        <aside className="product-auth-brand">
          <div className="product-auth-logo">
            <img src={LogoSrc} alt="" />
            <span>
              Resume <strong>Builder</strong>
            </span>
          </div>
          <div className="product-auth-brand-copy">
            <span className="product-auth-eyebrow">
              Secure account recovery
            </span>
            <h1>Get back to building your future.</h1>
            <p>
              We'll confirm it's really you, then help you securely create a new
              password.
            </p>
          </div>
          <div className="product-auth-trust">
            <SafetyCertificateOutlined />
            <span>
              <strong>Protected reset</strong>
              <small>Your verification code expires after 10 minutes.</small>
            </span>
          </div>
        </aside>
        <section
          className={`product-auth-card ${step === "reset" ? "product-auth-card-register" : ""}`}
        >
          <div
            className="product-auth-progress"
            aria-label="Password reset progress"
          >
            {["email", "verify", "reset"].map((item, index) => (
              <span
                key={item}
                className={
                  index <= ["email", "verify", "reset"].indexOf(step)
                    ? "active"
                    : ""
                }
              />
            ))}
          </div>
          <div className="product-auth-heading">
            <span>{content[step].eyebrow}</span>
            <h2>{content[step].title}</h2>
            <p>
              {content[step].body}
              {step === "verify" && (
                <>
                  {" "}
                  <strong>{email}</strong>.
                </>
              )}
            </p>
          </div>
          <Form layout="vertical" onFinish={onFinish}>
            {step === "email" && (
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  {
                    required: true,
                    type: "email",
                    message: "Please enter a valid email",
                  },
                ]}
              >
                <Input
                  size="large"
                  prefix={<MailOutlined />}
                  placeholder="you@company.com"
                  autoComplete="email"
                />
              </Form.Item>
            )}
            {step === "verify" && (
              <Form.Item
                label="Verification code"
                name="code"
                rules={[
                  {
                    required: true,
                    message: "Please enter the verification code",
                  },
                  {
                    pattern: /^\d{8}$/,
                    message: "The code must contain exactly 8 digits",
                  },
                ]}
              >
                <VerificationCodeInput disabled={loading} />
              </Form.Item>
            )}
            {step === "reset" && (
              <>
                <Form.Item
                  label="New Password"
                  name="password"
                  rules={[
                    {
                      required: true,
                      message: "Please enter your new password",
                    },
                    {
                      min: 6,
                      message: "Password must contain at least 6 characters",
                    },
                  ]}
                >
                  <Input.Password
                    size="large"
                    prefix={<LockOutlined />}
                    placeholder="Create a new password"
                    autoComplete="new-password"
                  />
                </Form.Item>
                <Form.Item
                  label="Confirm New Password"
                  name="confirmPassword"
                  dependencies={["password"]}
                  rules={[
                    {
                      required: true,
                      message: "Please confirm your new password",
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        return !value || getFieldValue("password") === value
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error("The passwords do not match"),
                            );
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    size="large"
                    prefix={<LockOutlined />}
                    placeholder="Repeat your new password"
                    autoComplete="new-password"
                  />
                </Form.Item>
              </>
            )}
            <Form.Item className="product-auth-submit">
              <Button
                type="primary"
                size="large"
                htmlType="submit"
                loading={loading}
              >
                {step === "email"
                  ? "Send Verification Code"
                  : step === "verify"
                    ? "Verify Code"
                    : "Update Password"}
              </Button>
            </Form.Item>
          </Form>
          <div className="product-auth-switch">
            <Button
              type="link"
              onClick={() =>
                navigate(step === "email" ? "/login" : "/forgot-password")
              }
            >
              {step === "email"
                ? "Back to sign in"
                : "Start over with another email"}
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ForgotPassword;
